import React from 'react';
import styled, { css } from 'styled-components';
import type { SeatMapOverview, SeatMapSlotResource } from '@src/core/buyerApiClient';
import { formatCents } from '../../common';
import { device } from '../../common/breakpoints';
import {
  BodyLarge,
  BodySmallMedium,
  BodyExtraSmall,
  BodyExtraSmallMedium,
  BodySmall,
  BodyRegular,
  BodyRegularMedium,
  Heading6,
} from '@src/fonts/typography/Typography';
import { useAppSelector } from '../../core/hooks';
import { useWidgetId } from '../../core/IdProvider';
import { adderResumeSelector } from '../../stores/product';
import { AllocatedResource } from '../../common/seatMaps';
import useTranslate from '../../i18n/useTranslate';
import { SimpleDivisor } from '@src/components/reusable';
import CheckboxIcon from '@src/icons/check-icon.svg';
import TrashIcon from '@src/icons/trash-icon.svg';
import { LayoutColors } from '@src/core/theme';

interface SeatMapSelectionProps {
  seatMap: SeatMapOverview;
  allocatedResources: AllocatedResource[];
  tariffSum: number;
  selectedCapacitySum: number;
  allAllocated: boolean;
  isReady: boolean;
  onRemove: (res: SeatMapSlotResource) => void;
}

const SeatMapSelection = (props: SeatMapSelectionProps) => {
  const { allocatedResources, tariffSum, selectedCapacitySum, allAllocated, isReady, onRemove } = props;
  const id = useWidgetId();
  const { resourcesPriceCents, totalCents, tariffsPriceCents } = useAppSelector(adderResumeSelector(id));
  const { i18n } = useTranslate();

  const handleRemoveResourceButtonClick = (resource: SeatMapSlotResource) => {
    onRemove(resource);
  };

  return (
    <SeatMapSelectionRoot>
      <SelectedResourcesContainer>
        <AllocationStatus>
          <small>{i18n('seatMap_pendingAllocationQuantity')}</small>
          <AllocationStatusCounter ready={isReady}>
            <AllocationStatusCounterText ready={isReady}>
              {Math.min(selectedCapacitySum, tariffSum)}
            </AllocationStatusCounterText>
            <AllocationStatusCounterText ready={isReady}>/</AllocationStatusCounterText>
            <AllocationStatusCounterText ready={isReady}>{tariffSum}</AllocationStatusCounterText>
            {isReady && (
              <CounterCheck>
                <CheckboxIcon />
              </CounterCheck>
            )}
          </AllocationStatusCounter>
        </AllocationStatus>
        {!allAllocated && allocatedResources.length > 0 && (
          <InfoNotice>{i18n('seatMap_pendingTariffAllocation')}</InfoNotice>
        )}
        <div>
          {allocatedResources.map(
            ({ resource, category, filledAmount, incomplete, shouldShowSubAllocationNotification }) => (
              <div key={resource.id}>
                <SelectedResource>
                  <ResourceMainInfo>
                    <ResourceName>
                      <p>{resource.name}</p>
                      <small>{category.name}</small>
                    </ResourceName>

                    <ResourceActions>
                      <RemoveButton onClick={() => handleRemoveResourceButtonClick(resource)}>
                        <TrashIcon />
                      </RemoveButton>
                    </ResourceActions>
                  </ResourceMainInfo>

                  <ResourceExtraInfo>
                    <ResourceCapacity>
                      {`${filledAmount}/${resource.capacity}`} {i18n('seatMap_filledCapacity')}
                    </ResourceCapacity>
                  </ResourceExtraInfo>
                </SelectedResource>

                {(!category.allowUnderAllocation && incomplete) || shouldShowSubAllocationNotification ? (
                  <ErrorNotice>{i18n('seatMap_resourceSubAllocationNotice')}</ErrorNotice>
                ) : null}
              </div>
            ),
          )}
        </div>
      </SelectedResourcesContainer>
      <SimpleDivisor margin={16} />
      <div>
        {resourcesPriceCents > 0 ? (
          <>
            <ResourcePriceDisplay>
              <ResourcePriceDisplayLabel>{i18n('seatMap_tariff_price_cents')}</ResourcePriceDisplayLabel>
              <ResourcePriceDisplayPrice>{formatCents(tariffsPriceCents)}</ResourcePriceDisplayPrice>
            </ResourcePriceDisplay>

            <ResourcePriceDisplay>
              <ResourcePriceDisplayLabel>{i18n('seatMap_resourceReservationPrice')}</ResourcePriceDisplayLabel>
              <ResourcePriceDisplayPrice>+{formatCents(resourcesPriceCents)}</ResourcePriceDisplayPrice>
            </ResourcePriceDisplay>
          </>
        ) : null}

        <TotalPriceDisplay>
          <TotalPriceDisplayLabel>{i18n('seatMap_totalPrice')}</TotalPriceDisplayLabel>
          <TotalPriceDisplayPrice>{formatCents(totalCents)}</TotalPriceDisplayPrice>
        </TotalPriceDisplay>
      </div>
    </SeatMapSelectionRoot>
  );
};

const SeatMapSelectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const FeatureBox = css`
  padding: 8px;
  background-color: transparent;
  border-radius: 5px;
  border: 1px solid #e0e0e0;
`;

const AllocationStatus = styled.div`
  display: flex;
  align-items: center;

  small {
    flex: 1;
    ${BodyExtraSmallMedium};
  }
`;

const SelectedResourcesContainer = styled.div`
  flex: auto;
`;

interface AllocationStatusCounterProps {
  ready: boolean;
}

const AllocationStatusCounter = styled.div<AllocationStatusCounterProps>`
  padding: 4px;
  border-radius: 4px;
  background-color: ${({ ready }) => (ready ? '#D7EFE4' : 'transparent')};
`;

const AllocationStatusCounterText = styled.span<AllocationStatusCounterProps>`
  ${BodyRegular};
  ${device.smallTablet} {
    ${BodyLarge};
    color: ${({ ready }) => (ready ? '#2D996F' : 'inherit')};
  }
  color: ${({ ready }) => (ready ? '#2D996F' : 'inherit')};
`;

const CounterCheck = styled.span`
  display: inline-block;
  box-sizing: border-box;
  margin-left: 8px;
  padding: 6px 0;
  width: 32px;
  height: 30px;
  border-radius: 4px;
  background-color: #2d996f;
  color: #ffffff;
  font-size: 11px;
  line-height: 11px;
  text-align: center;

  > svg path {
    stroke: #ffffff;
  }
`;

const BaseNotice = css`
  ${BodyExtraSmallMedium};
  margin: 8px auto;
  padding: 8px 12px;
  border-radius: 4px;
`;
const InfoNotice = styled.div`
  ${BaseNotice};
  background-color: #c7e5fd;
  color: #2097f6;
`;
const ErrorNotice = styled.div`
  ${BaseNotice};
  background-color: #ffd2cd;
  color: #de1306;
`;

const SelectedResource = styled.div`
  ${FeatureBox};
  margin: 14px auto;
`;
const ResourceMainInfo = styled.div`
  display: flex;
`;
const ResourceName = styled.span`
  flex: auto;
  ${BodySmallMedium};

  p {
    word-break: break-all;
  }
`;

const ResourceExtraInfo = styled.div`
  display: flex;
  margin-top: 10px;
  align-items: end;
`;
const ResourceCapacity = styled.span`
  flex: auto;
  ${BodyExtraSmall};
`;
const ResourceActions = styled.span`
  flex: inital;
`;
const RemoveButton = styled.button`
  width: 34px;
  height: 34px;
  padding: 5px;
  border: 1px solid #616161;
  box-sizing: border-box;
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;

  > svg path {
    stroke: #616161;
  }
`;

const ResourcePriceDisplay = styled.div`
  display: flex;
  ${BodySmall};
  color: ${LayoutColors.blackDark};
`;
const ResourcePriceDisplayLabel = styled.span`
  flex: auto;
`;
const ResourcePriceDisplayPrice = styled.span`
  flex: initial;
`;

const TotalPriceDisplay = styled.div`
  display: flex;
  align-items: center;
`;
const TotalPriceDisplayLabel = styled.div`
  flex: auto;
  ${Heading6};
  color: ${LayoutColors.blackDark};
`;
const TotalPriceDisplayPrice = styled.div`
  flex: initial;
  ${BodyRegularMedium};
  color: ${LayoutColors.blackDark};
`;

export default SeatMapSelection;
